import { Clear } from '@mui/icons-material';
import { Chip, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { ContainerWithVerticalScrollShadow, FormField } from '../../../components/Form/FormComponents';
import { BasicDragHandle, SortableItem } from '../../../components/SortableList/SortableItem';
import { SortableList } from '../../../components/SortableList/SortableList';
import { KpiPeriod } from '../../../data-models/company-financials.data-model';
import { MprEmptyListText, MprPanelHeading } from './MprPanelHeading';
import { lastMprPeriodLength, MprPeriodPicker } from './MprPeriodPicker';
import { MprPeriodRangeForm } from './MprPeriodRangeForm';
import { mprListContainerCss, mprListRowCss } from './mprSettingsStyles';
import { ReportingPeriodType } from './MultiPeriodReporting.schemas';
import {
  getFormattedConfigPeriod,
  getMprPeriodKey,
  getResolvedMprPeriod,
  MprPeriod,
  uniqueMprPeriods,
} from './multiPeriodReportingUtils';
import { useMprColumnConfigs } from './useMprColumnConfigs';

type PeriodWithId = MprPeriod & { id: string };

const PeriodPickerLabel = 'Pick periods';

export function MprPeriodsPanel() {
  const { draft, setDraft } = useMprColumnConfigs();
  const [length, setLength] = useState<KpiPeriod>(() => lastMprPeriodLength(draft.periods));

  const periods: PeriodWithId[] = useMemo(
    () => draft.periods.map((period) => ({ ...period, id: getMprPeriodKey(period) })),
    [draft.periods]
  );

  const onReorder = useCallback(
    (reordered: PeriodWithId[]) => {
      setDraft((prev) => ({ ...prev, periods: reordered.map(({ id, ...period }) => period) }));
    },
    [setDraft]
  );
  const onRemove = useCallback(
    (id: string) => {
      setDraft((prev) => ({
        ...prev,
        periods: prev.periods.filter((period) => getMprPeriodKey(period) !== id),
      }));
    },
    [setDraft]
  );
  const onPeriodsChange = useCallback(
    (next: MprPeriod[]) => {
      setDraft((prev) => ({ ...prev, periods: uniqueMprPeriods(next) }));
    },
    [setDraft]
  );

  return (
    <Stack gap='0.5rem' minWidth={0}>
      <MprPanelHeading title='When' hint='Every item is shown for every period' />
      <FormField>
        <MprPeriodPicker
          label={PeriodPickerLabel}
          selected={draft.periods}
          onChange={onPeriodsChange}
          length={length}
          onLengthChange={setLength}
        />
      </FormField>
      <MprPeriodRangeForm length={length} existing={draft.periods} onAdd={onPeriodsChange} />
      {periods.length === 0 ? (
        <MprEmptyListText css={mprListContainerCss}>
          No periods yet. Pick a rolling or fixed period.
        </MprEmptyListText>
      ) : (
        <ContainerWithVerticalScrollShadow css={mprListContainerCss} role='group' aria-label='Periods'>
          <SortableList
            items={periods}
            onChange={onReorder}
            renderItem={(period) => (
              <SortableItem id={period.id}>
                <PeriodRow period={period} onRemove={onRemove} />
              </SortableItem>
            )}
          />
        </ContainerWithVerticalScrollShadow>
      )}
    </Stack>
  );
}

function PeriodRow({ period, onRemove }: { period: PeriodWithId; onRemove: (id: string) => void }) {
  const label = getFormattedConfigPeriod(period);
  const rolling = period.type === ReportingPeriodType.relative;
  return (
    <Typography component='div' variant='body2' css={mprListRowCss}>
      <BasicDragHandle />
      <div>
        {label}
        {rolling && (
          <Typography component='span' variant='caption' color='text.secondary'>
            {` · ${getResolvedMprPeriod(period)}`}
          </Typography>
        )}
      </div>
      <Chip size='small' label={rolling ? 'Rolling' : 'Fixed'} />
      <IconButton aria-label={`Remove ${label}`} onClick={() => onRemove(period.id)}>
        <Clear fontSize='small' />
      </IconButton>
    </Typography>
  );
}
